/**
 * BlogReshareDialog — "Smart re-share" workflow.
 *
 * Why this exists:
 *   Publishing a blog post is only half the distribution job — admins
 *   still want to cross-post to FB, IG, Threads, LinkedIn, and X. Doing
 *   that by hand means re-writing five different captions, picking the
 *   right hashtag mix per channel, and re-uploading the cover. Tedious
 *   and skip-prone.
 *
 *   This dialog handles all of that in one click:
 *   1. Admin picks which channels to target (all 5 by default).
 *   2. Backend `/api/admin/social/from-blog` generates a per-platform
 *      caption tuned to each channel's tone + length, reuses the post's
 *      cover image, and stores one social_draft per platform.
 *   3. We land the admin straight on the SMG tab — they can review,
 *      tweak, schedule, or one-click publish each draft from there.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Share2, Loader2, Check, AlertCircle, ExternalLink } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const ALL_PLATFORMS = [
  { id: 'Instagram', label: 'Instagram', sub: 'Square + 10 hashtags' },
  { id: 'Facebook', label: 'Facebook', sub: 'Story-driven + link unfurl' },
  { id: 'Threads', label: 'Threads', sub: 'Short + conversational' },
  { id: 'LinkedIn', label: 'LinkedIn', sub: 'Professional + auto-card' },
  { id: 'Twitter', label: 'X (Twitter)', sub: '270 char single tweet' },
];

const BlogReshareDialog = ({ open, onOpenChange, post, onSuccess }) => {
  // All five channels picked by default — admins typically want a full
  // cross-post. Untick whatever doesn't fit a given piece.
  const [picked, setPicked] = useState(
    () => new Set(ALL_PLATFORMS.map((p) => p.id)),
  );
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const togglePlatform = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setPicked(new Set(ALL_PLATFORMS.map((p) => p.id)));
    setResult(null);
    setRunning(false);
  };

  const handleClose = (next) => {
    if (running) return; // don't allow close while a generation is in flight
    if (!next) reset();
    onOpenChange(next);
  };

  const run = async () => {
    if (!post?.post_id) return;
    if (picked.size === 0) {
      toast.error('Pick at least one platform.');
      return;
    }
    setRunning(true);
    setResult(null);
    const { data, error } = await apiFetch('/api/admin/social/from-blog', {
      method: 'POST',
      body: {
        post_id: post.post_id,
        platforms: Array.from(picked),
      },
    });
    setRunning(false);
    if (error) {
      toast.error(error);
      return;
    }
    setResult(data);
    if (data?.drafts?.length) {
      toast.success(
        `Generated ${data.drafts.length} draft${data.drafts.length === 1 ? '' : 's'} — review & publish from the Social tab.`,
      );
      if (onSuccess) onSuccess(data);
    }
    if (data?.errors?.length) {
      toast.error(
        `Some channels failed: ${data.errors.map((e) => e.platform).join(', ')}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg"
        data-testid="blog-reshare-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-[#00A896]" />
            Smart re-share
          </DialogTitle>
          <DialogDescription>
            One click to cross-post <span className="font-medium text-foreground">{post?.title}</span>{' '}
            to your social channels. Each platform gets its own AI-tuned caption.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Pick channels
              </Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ALL_PLATFORMS.map((p) => {
                  const on = picked.has(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlatform(p.id)}
                      aria-pressed={on}
                      data-testid={`reshare-platform-${p.id.toLowerCase()}`}
                      className={`text-left p-2.5 rounded-md border transition ${
                        on
                          ? 'bg-[#00A896]/10 border-[#00A896]/50'
                          : 'bg-background hover:bg-muted border-muted-foreground/20'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <div
                          className={`h-4 w-4 rounded border flex items-center justify-center transition ${
                            on
                              ? 'bg-[#00A896] border-[#00A896]'
                              : 'bg-background border-muted-foreground/40'
                          }`}
                          aria-hidden="true"
                        >
                          {on && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className="text-sm font-medium">{p.label}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground pl-6">
                        {p.sub}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Drafts land in your Social Media Generator tab. The blog&apos;s cover
                image is reused as the visual for every channel.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="blog-reshare-result">
            <div className="flex items-center gap-2 text-sm font-medium text-[#00A896]">
              <Check className="h-4 w-4" />
              {result.drafts.length} draft{result.drafts.length === 1 ? '' : 's'} created
            </div>
            <div className="space-y-1.5">
              {result.drafts.map((d) => (
                <div
                  key={d.draft_id}
                  className="flex items-center gap-2 p-2 rounded border bg-muted/30"
                  data-testid={`reshare-draft-${d.platform.toLowerCase()}`}
                >
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {d.platform}
                  </Badge>
                  <p className="text-xs text-foreground line-clamp-1 flex-1 min-w-0">
                    {d.caption.split('\n')[0].slice(0, 80)}…
                  </p>
                </div>
              ))}
              {result.errors?.length > 0 && (
                <div className="mt-2 p-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-500/10">
                  <div className="flex items-center gap-2 text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {result.errors.length} channel{result.errors.length === 1 ? '' : 's'} failed
                  </div>
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-[11px] text-amber-700 dark:text-amber-300 pl-5">
                      {e.platform}: {e.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row gap-2 justify-end">
          {!result ? (
            <>
              <Button
                variant="ghost"
                onClick={() => handleClose(false)}
                disabled={running}
                data-testid="blog-reshare-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={run}
                disabled={running || picked.size === 0}
                className="bg-[#00A896] hover:bg-[#008b7a]"
                data-testid="blog-reshare-generate"
              >
                {running ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Writing {picked.size} caption{picked.size === 1 ? '' : 's'}…
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4 mr-1.5" />
                    Generate {picked.size} draft{picked.size === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => handleClose(false)}
                data-testid="blog-reshare-close"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  handleClose(false);
                  // Open Social Media Generator tab so the admin can
                  // review / publish each draft straight away.
                  window.location.href = '/admin?tab=social-gen';
                }}
                className="bg-[#00A896] hover:bg-[#008b7a]"
                data-testid="blog-reshare-go-to-social"
              >
                <ExternalLink className="h-4 w-4 mr-1.5" />
                Open Social tab
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlogReshareDialog;
